<!--
  Upload Overlay, visible on File Upload or Diagram Generation.
  Also has logic to check if a file has been uploaded already.
  Triggered by MainMenu#handleFileUpload and ProgressBar#verifyGetDiagrams->ClaimsAndGraphPanel#handlegenerateDraftFromClaims
-->

<template>
  <v-overlay
    :value="showOverlay"
    :opacity="inserting ? 0 : 0.75"
    :class="inserting ? 'loadingCursor' : !showCard && dropUpload ? 'prevent' : ''"
    @dragleave.native.prevent="setDropState"
    @drag.prevent
    @dragover.prevent
    @click="skipSequentialLoad"
  >
    <!--    <v-fade-transition hide-on-leave>-->
    <img
      v-if="showImage"
      src="@/data/static/Drag and Drop Icon.svg"
      alt="drag and drop"
      @dragenter.prevent
      @dragover.prevent
      @dragleave.prevent
    />
    <!--    </v-fade-transition>-->
    <v-fade-transition hide-on-leave>
      <UploadProgress v-if="uploading" />
    </v-fade-transition>
    <v-fade-transition hide-on-leave>
      <Loading v-if="loading && !inserting" />
    </v-fade-transition>
    <v-fade-transition hide-on-leave>
      <PromptCard
        v-if="showCard"
        title="Overwrite document?"
        text="You will lose your progress on the current document when you start a new one."
        @okay="handleFileUpload"
        @cancel="setDropState"
        colors
        okayText="Overwrite"
      />
    </v-fade-transition>
  </v-overlay>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex';
import UploadProgress from './UploadProgress.vue';
import Loading from '../global/Loading.vue';
import PromptCard from '../global/PromptCard';
import { ErrorKeys as EK } from '@/data/constants';
import { unpackClaims } from '@/support/utilities/unpackers';

export default {
  components: {
    UploadProgress,
    Loading,
    PromptCard,
  },
  name: 'UploadOverlay',
  data() {
    return {
      ignore: false,
      promptButtons: [
        {
          text: 'Cancel',
          action: 'Cancel',
        },
      ],
    };
  },
  computed: {
    ...mapState({
      dropUpload: (state) => state.upload.drop,
      file: (state) => state.upload.file,
      uploading: (state) => state.upload.uploading,
      loading: (state) => state.draft.loading,
      claims: (state) => state.draft.claims,
      step: (state) => state.draft.step,
      prompt: (state) => state.upload.dialog,
      inserting: (state) => state.draft.insertingDocData,
      showWorkspace: (state) => state.draft.showWorkspace,
    }),
    /**
     * @returns {Boolean}
     */
    showCard() {
      return (
        (this.file &&
          !this.uploading &&
          this.claims.length > 0 &&
          !this.ignore &&
          !this.inserting) ||
        this.prompt
      );
    },

    /**
     * @returns {Boolean}
     */
    showOverlay() {
      return this.dropUpload || this.loading || this.uploading || this.inserting;
    },

    /**
     * @returns {Boolean}
     */
    showImage() {
      return !this.file && !this.loading && !this.uploading && !this.prompt;
    },
  },
  methods: {
    ...mapMutations({
      resetState: 'draft/resetState',
      setClaims: 'draft/setClaims',
      changeClaims: 'draft/changeClaims',
      setUploadState: 'upload/setUploadState',
      toggleDialog: 'upload/toggleDialog',
      endUpload: 'upload/endUpload',
      setLoading: 'draft/setLoading',
      setError: 'error/setErrorState',
      setShowWorkspace: 'draft/setShowWorkspace',
      startSequentialLoad: 'draft/startSequentialLoad',
      stopSequentialLoad: 'draft/stopSequentialLoad',
    }),
    ...mapActions({
      generateDraft: 'draft/generateDraft',
      uploadFile: 'upload/uploadFile',
      loadClaims: 'draft/loadClaims',
      skipSequentialLoad: 'draft/skipSequentialLoad',
    }),

    handleDrag() {
      this.setUploadState({ key: 'drop', val: true });
    },

    setDropState() {
      this.setUploadState({ key: 'drop', val: false });
      this.setUploadState({ key: 'dialog', val: false });
      this.setUploadState({ key: 'file', val: null });
    },

    startNewDocument() {
      this.toggleDialog({ val: false });
      this.resetState();
    },

    async handleFileUpload() {
      document.activeElement.blur();
      if (this.file !== null) {
        try {
          const doc = await this.uploadFile();
          const claims = unpackClaims(doc);
          this.endUpload();
          if (this.step === 2) {
            this.startSequentialLoad();
            await this.generateDraft(claims);
            this.stopSequentialLoad();
          } else {
            await this.loadClaims(claims);
          }
          if (!this.showWorkspace) {
            this.setShowWorkspace(true);
          }
        } catch (err) {
          console.error(err);
          this.setError({
            key: 'error',
            val: true,
            message: EK.UPLOAD_ERROR,
          });
          this.endUpload();
        }
      } else {
        this.startNewDocument();
      }
    },
  },

  watch: {
    file: {
      handler(file) {
        this.ignore = false;
        if (this.claims.length === 0 && file !== null) {
          this.handleFileUpload();
        }
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.loadingCursor {
  cursor: wait;
}

img {
  //border: 2px solid red;
  //background: white;
  position: relative;
}
</style>
